import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { showsScoreAndPercent } from '@/lib/assessment-score-display';
import { assessmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { libraryOriginBack, withLibraryContext } from '@/lib/library-navigation';
import { QUESTION_TYPE_OPTIONS } from '@/features/questions/questionEditor';

type DetailTab = 'info' | 'questions' | 'versions' | 'courses';

interface AssessmentSettings {
  type?: string;
  max_questions?: number;
  min_questions_to_pass?: number;
  require_passing_score?: boolean;
  show_score_and_percent?: boolean;
  time_limit_minutes?: number;
  unlimited_attempts?: boolean;
  max_attempts?: number | null;
  randomize_questions?: boolean;
  show_answers?: boolean;
  auto_grade?: boolean;
}

interface AssessmentDetail {
  id: string;
  title: string;
  slug: string;
  code: string;
  status: string;
  type: string;
  description?: string | null;
  currentVersionId?: string | null;
  versionCount: number;
  bankQuestionCount: number;
  questionsPerAttempt: number;
  questionBank?: { id: string; title: string } | null;
  currentVersion?: { id: string; versionNumber: number; settings?: AssessmentSettings | null } | null;
  questions: { id: string; title: string; type: string; sortOrder: number }[];
  versions: { id: string; versionNumber: number; versionLabel: string; createdAt: string; isCurrent: boolean }[];
  usages: { id: string; courseId: string; courseName: string; sectionTitle: string; itemTitle: string }[];
  tags: string[];
  category?: string | null;
  creatorName?: string | null;
  learningObjectId?: string | null;
}

const TYPE_META: Record<string, { label: string; icon: string }> = {
  exam: { label: 'Examen', icon: 'assignment' },
  quiz: { label: 'Quiz', icon: 'quiz' },
  survey: { label: 'Encuesta', icon: 'poll' },
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicada',
  archived: 'Archivada',
};

const STATUS_ICONS: Record<string, string> = {
  draft: 'edit_document',
  published: 'check_circle',
  archived: 'archive',
};

function statusModifier(status: string): string {
  if (status === 'published') return 'published';
  if (status === 'archived') return 'archived';
  return 'draft';
}

function questionTypeLabel(type: string): string {
  return QUESTION_TYPE_OPTIONS.find((item) => item.value === type)?.label ?? type;
}

function formatVersionDate(value: string): string {
  return new Date(value).toLocaleString('es', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAttempts(settings: AssessmentSettings | null | undefined): string {
  if (!settings) return '—';
  if (settings.unlimited_attempts) return 'Ilimitados';
  return settings.max_attempts != null ? String(settings.max_attempts) : '—';
}

export function AssessmentShowPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const back = libraryOriginBack(searchParams, '/evaluaciones', 'Volver a evaluaciones');

  const canPublish = hasPermission(PERMISSIONS.EVALUACIONES_PUBLICAR);
  const canEdit = hasPermission(PERMISSIONS.EVALUACIONES_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.EVALUACIONES_ELIMINAR);
  const canView = hasPermission(PERMISSIONS.EVALUACIONES_VER);

  const { data, isLoading, error } = useQuery({
    queryKey: ['assessment', id],
    queryFn: () => api<AssessmentDetail>(`/assessments/${id}`),
    enabled: Boolean(id),
  });

  const publishMutation = useMutation({
    mutationFn: () => api(`/assessments/${id}/publish`, { method: 'POST' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['assessment', id] });
      void queryClient.invalidateQueries({ queryKey: ['assessments'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/assessments/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      toast(assessmentSuccessToast('delete'));
      void queryClient.invalidateQueries({ queryKey: ['assessments'] });
      void queryClient.invalidateQueries({ queryKey: ['library'] });
      navigate('/evaluaciones');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la evaluación'));
    },
  });

  if (isLoading) {
    return (
      <div className="lms-assessment-show font-sans">
        <div className="flex justify-center py-24">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="lms-assessment-show font-sans">
        <div className="lms-site-admin-hub-container lms-assessment-show__body py-12">
          <p className="text-sm text-red-600" role="alert">
            {(error as Error)?.message ?? 'Evaluación no encontrada'}
          </p>
          <Link to={back.to} className="lms-assessment-show__btn lms-assessment-show__btn--outline-sm mt-4 inline-flex">
            Volver al catálogo
          </Link>
        </div>
      </div>
    );
  }

  const settings = (data.currentVersion?.settings ?? {}) as AssessmentSettings;
  const typeMeta = TYPE_META[data.type] ?? TYPE_META.exam;
  const statusMod = statusModifier(data.status);
  const passingScore = settings.min_questions_to_pass;
  const timeLimitValue = settings.time_limit_minutes;
  const timeLimitUnit = timeLimitValue === 1 ? 'minuto' : 'minutos';
  const courseCount = data.usages.length;
  const coursesLabel = courseCount === 1 ? 'curso' : 'cursos';
  const deleteBlocked = data.usages.length > 0;

  return (
    <div className="lms-assessment-show font-sans">
      <header className="lms-assessment-show__hero">
        <div className="lms-assessment-show__hero-pattern" aria-hidden="true" />
        <div className="lms-site-admin-hub-container lms-assessment-show__hero-inner">
          <div className="lms-assessment-show__hero-row">
            <div className="lms-assessment-show__hero-copy">
              <div className="flex items-start gap-4">
                <HubBackLink to={back.to} label={back.label} />
                <div className="min-w-0">
                  <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">{data.title}</h1>
                  <p className="lms-assessment-show__subtitle">
                    Evaluación reutilizable · {data.code}
                  </p>
                </div>
              </div>
            </div>

            <div className="lms-assessment-show__hero-actions">
              <div className="lms-assessment-show__icon-actions">
                {canPublish && data.status !== 'published' && (
                  <button
                    type="button"
                    className="lms-assessment-show__icon-btn"
                    title="Publicar"
                    disabled={publishMutation.isPending}
                    onClick={() => publishMutation.mutate()}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      publish
                    </span>
                  </button>
                )}
                {canEdit && (
                  <button type="button" className="lms-assessment-show__icon-btn" title="Duplicar" disabled>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      file_copy
                    </span>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="lms-assessment-show__icon-btn lms-assessment-show__icon-btn--danger"
                    title="Eliminar"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      delete
                    </span>
                  </button>
                )}
              </div>

              {canView && (
                <Link
                  to={withLibraryContext(`/evaluaciones/${data.id}/preview`, searchParams)}
                  className="lms-assessment-show__btn lms-assessment-show__btn--ghost"
                >
                  <span className="material-symbols-outlined" aria-hidden="true">
                    visibility
                  </span>
                  Vista previa
                </Link>
              )}
              {canEdit && (
                <Link to={withLibraryContext(`/evaluaciones/${data.id}/edit`, searchParams)} className="lms-assessment-show__btn lms-assessment-show__btn--primary">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    edit
                  </span>
                  Editar
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="lms-assessment-show__tabs">
        <div className="lms-site-admin-hub-container">
          <nav className="lms-assessment-show__tabs-nav" aria-label="Secciones de la evaluación">
            {(
              [
                ['info', 'Información'],
                ['questions', 'Preguntas'],
                ['versions', 'Versiones'],
                ['courses', 'Cursos relacionados'],
              ] as const
            ).map(([tab, label]) => (
              <button
                key={tab}
                type="button"
                className={`lms-assessment-show__tab${activeTab === tab ? ' lms-assessment-show__tab--active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {label}
                {tab === 'courses' ? <span className="lms-assessment-show__tab-count">{courseCount}</span> : null}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="lms-site-admin-hub-container lms-assessment-show__body">
        {activeTab === 'info' && (
          <div className="lms-assessment-show__info-grid">
            <div className="lms-assessment-show__info-main">
              <section className="lms-assessment-show__desc">
                <h2>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    info
                  </span>
                  Descripción
                </h2>
                <p>{data.description?.trim() || 'Sin descripción.'}</p>
              </section>

              <section>
                <h3 className="lms-assessment-show__section-label">Configuración de la Evaluación</h3>
                <div className="lms-assessment-show__bento">
                  <article className="lms-assessment-show__tile">
                    <div className="lms-assessment-show__tile-icon lms-assessment-show__tile-icon--accent">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        verified
                      </span>
                    </div>
                    <p className="lms-assessment-show__tile-label">Mínimo para aprobar</p>
                    {settings.require_passing_score === false ? (
                      <p className="lms-assessment-show__tile-value">No exigido</p>
                    ) : passingScore != null ? (
                      <p className="lms-assessment-show__tile-value">
                        {passingScore}
                        <span>preguntas</span>
                      </p>
                    ) : (
                      <p className="lms-assessment-show__tile-empty">—</p>
                    )}
                  </article>

                  <article className="lms-assessment-show__tile">
                    <div className="lms-assessment-show__tile-icon lms-assessment-show__tile-icon--accent">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        timer
                      </span>
                    </div>
                    <p className="lms-assessment-show__tile-label">Tiempo límite</p>
                    {timeLimitValue ? (
                      <p className="lms-assessment-show__tile-value">
                        {timeLimitValue}
                        <span>{timeLimitUnit}</span>
                      </p>
                    ) : (
                      <p className="lms-assessment-show__tile-value">Sin límite</p>
                    )}
                  </article>

                  <article className="lms-assessment-show__tile">
                    <div className="lms-assessment-show__tile-icon lms-assessment-show__tile-icon--accent">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        all_inclusive
                      </span>
                    </div>
                    <p className="lms-assessment-show__tile-label">Intentos</p>
                    <p className="lms-assessment-show__tile-value">{formatAttempts(settings)}</p>
                  </article>

                  <article className="lms-assessment-show__tile lms-assessment-show__tile--wide">
                    <div className="lms-assessment-show__tile-icon">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        format_list_numbered
                      </span>
                    </div>
                    <p className="lms-assessment-show__tile-label">Preguntas por intento</p>
                    {settings.max_questions ? (
                      <p className="lms-assessment-show__tile-value">{settings.max_questions}</p>
                    ) : (
                      <p className="lms-assessment-show__tile-empty">—</p>
                    )}
                  </article>

                  <article className="lms-assessment-show__tile">
                    <div className="lms-assessment-show__tile-icon">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        folder_open
                      </span>
                    </div>
                    <p className="lms-assessment-show__tile-label">Banco</p>
                    {data.questionBank?.title ? (
                      <p className="lms-assessment-show__tile-value lms-assessment-show__tile-value--sm">
                        {data.questionBank.title}
                      </p>
                    ) : (
                      <p className="lms-assessment-show__tile-empty">—</p>
                    )}
                  </article>
                </div>
              </section>
            </div>

            <aside className="lms-assessment-show__sidebar">
              <div className="lms-assessment-show__details">
                <h3>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    tune
                  </span>
                  Detalles
                </h3>
                <ul>
                  <li className="lms-assessment-show__details-badges">
                    <div className="lms-assessment-show__badges">
                      <span className="lms-assessment-show__badge lms-assessment-show__badge--type">
                        <span className="material-symbols-outlined" aria-hidden="true">
                          {typeMeta.icon}
                        </span>
                        {typeMeta.label}
                      </span>
                      <span
                        className={`lms-assessment-show__badge lms-assessment-show__badge--status lms-assessment-show__badge--status-${statusMod}`}
                      >
                        <span className="material-symbols-outlined" aria-hidden="true">
                          {STATUS_ICONS[statusMod] ?? STATUS_ICONS.draft}
                        </span>
                        {STATUS_LABELS[data.status] ?? data.status}
                      </span>
                    </div>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        history
                      </span>
                      Versión
                    </span>
                    <span className="lms-assessment-show__details-value">v{data.versionCount}</span>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        school
                      </span>
                      Cursos vinculados
                    </span>
                    <span className="lms-assessment-show__details-value">
                      {courseCount} {coursesLabel}
                    </span>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        percent
                      </span>
                      Calificación y %
                    </span>
                    <span className="lms-assessment-show__details-value">
                      {showsScoreAndPercent(settings) ? 'Visible' : 'Oculta'}
                    </span>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        fact_check
                      </span>
                      Corrección
                    </span>
                    <span className="lms-assessment-show__details-value">
                      {settings.auto_grade === false ? 'Manual' : 'Automática'}
                    </span>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        source
                      </span>
                      Preguntas
                    </span>
                    <span className="lms-assessment-show__details-value">
                      {data.bankQuestionCount} en banco
                    </span>
                  </li>
                  <li>
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        category
                      </span>
                      Categoría
                    </span>
                    <span
                      className={`lms-assessment-show__details-value${!data.category ? ' lms-assessment-show__details-value--muted' : ''}`}
                    >
                      {data.category ?? '—'}
                    </span>
                  </li>
                  <li className="lms-assessment-show__details-tags">
                    <span className="lms-assessment-show__details-label">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        sell
                      </span>
                      Etiquetas
                    </span>
                    {data.tags.length > 0 ? (
                      <div className="lms-assessment-show__tags">
                        {data.tags.map((tag) => (
                          <span key={tag}>{tag}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="lms-assessment-show__details-value lms-assessment-show__details-value--muted">
                        —
                      </span>
                    )}
                  </li>
                </ul>
              </div>
            </aside>
          </div>
        )}

        {activeTab === 'questions' && (
          <section className="lms-assessment-show__panel">
            {data.questions.length > 0 ? (
              data.questions.map((question) => (
                <div key={question.id} className="lms-assessment-show__list-row">
                  <div>
                    <p className="lms-assessment-show__list-title">{question.title}</p>
                    <p className="lms-assessment-show__list-meta">{questionTypeLabel(question.type)}</p>
                  </div>
                  <span className="lms-assessment-show__list-badge">#{question.sortOrder + 1}</span>
                </div>
              ))
            ) : (
              <div className="lms-assessment-show__empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  help
                </span>
                <h2>Sin preguntas</h2>
                <p>Selecciona un banco al crear o editar la evaluación.</p>
              </div>
            )}
          </section>
        )}

        {activeTab === 'versions' && (
          <section className="lms-assessment-show__panel">
            {data.versions.length > 0 ? (
              data.versions.map((version) => (
                <div key={version.id} className="lms-assessment-show__list-row">
                  <div>
                    <p className="lms-assessment-show__list-title">{version.versionLabel}</p>
                    <p className="lms-assessment-show__list-meta">{formatVersionDate(version.createdAt)}</p>
                  </div>
                  {version.isCurrent ? (
                    <span className="lms-assessment-show__list-badge lms-assessment-show__list-badge--success">
                      Vigente
                    </span>
                  ) : canPublish ? (
                    <button type="button" className="lms-assessment-show__btn lms-assessment-show__btn--outline-sm" disabled>
                      Restaurar
                    </button>
                  ) : null}
                </div>
              ))
            ) : (
              <div className="lms-assessment-show__empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  history
                </span>
                <h2>Sin versiones</h2>
                <p>Aún no hay historial de versiones para esta evaluación.</p>
              </div>
            )}
          </section>
        )}

        {activeTab === 'courses' && (
          <section className="lms-assessment-show__panel">
            {data.usages.length > 0 ? (
              data.usages.map((usage) => (
                <div key={usage.id} className="lms-assessment-show__list-row">
                  <div>
                    <p className="lms-assessment-show__list-title">{usage.courseName}</p>
                    <p className="lms-assessment-show__list-meta">
                      {usage.sectionTitle} · {usage.itemTitle}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="lms-assessment-show__empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  school
                </span>
                <h2>Sin cursos relacionados</h2>
                <p>Esta evaluación aún no está referenciada en ningún curso.</p>
              </div>
            )}
          </section>
        )}
      </div>

      {canDelete && (
        <ConfirmModal
          open={deleteOpen}
          title="Eliminar evaluación"
          description={
            deleteBlocked
              ? `Esta evaluación está en ${data.usages.map((usage) => `«${usage.courseName}»`).join(', ')}. Quítala del constructor del curso antes de eliminarla.`
              : `¿Está seguro de eliminar la evaluación «${data.title}»? Esta acción no se puede deshacer.`
          }
          confirmLabel={deleteBlocked ? 'Ver cursos' : 'Eliminar'}
          cancelLabel={deleteBlocked ? 'Cerrar' : 'Cancelar'}
          variant={deleteBlocked ? 'primary' : 'danger'}
          loading={deleteMutation.isPending}
          onConfirm={() => {
            if (deleteBlocked) {
              setDeleteOpen(false);
              toast({
                title: 'No se puede eliminar una evaluación utilizada en cursos.',
                variant: 'danger',
              });
              setActiveTab('courses');
              return;
            }
            deleteMutation.mutate();
          }}
          onClose={() => {
            if (!deleteMutation.isPending) setDeleteOpen(false);
          }}
        />
      )}
    </div>
  );
}
